/**
 * Cookie utilities for cross-domain token sharing
 * Supports sharing tokens between subdomains:
 * - localhost: Development environment (no cross-domain sharing)
 * - uat: ${clientname}.uat.hq.ansr.com ↔ uat.hq.ansr.com
 * - sandbox: ${clientname}.sandbox.hq.ansr.com ↔ sandbox.hq.ansr.com
 * - prod: ${clientname}.hq.ansr.com ↔ hq.ansr.com
 *
 * LOCALHOST DEVELOPMENT:
 * - Cookies are set without domain attribute (local to current domain)
 * - Secure flag is disabled to allow HTTP
 * - SameSite is set to 'lax' for better compatibility
 * - No cross-domain sharing (not needed for localhost)
 *
 * USAGE:
 * - For localhost: Cookies work normally without any special configuration
 * - For production: Cookies are shared across subdomains automatically
 */

// Cookie names
const TOKEN_COOKIE_NAME = 'authToken';
const COMPANY_REFERER_COOKIE_NAME = 'companyReferer';

// Environment configurations
const ENVIRONMENTS = {
    localhost: {
        env: 'localhost',
        domain: '', // No domain for localhost - cookies will be set for current domain only
        secure: false, // Allow HTTP for localhost
        sameSite: 'lax', // More permissive for localhost development
    },
    uat: {
        env: 'uat',
        domain: '.uat.hq.ansr.com', // This allows sharing between *.uat.hq.ansr.com and uat.hq.ansr.com
        secure: true,
        sameSite: 'strict',
    },
    sandbox: {
        env: 'sandbox',
        domain: '.sandbox.hq.ansr.com', // This allows sharing between *.sandbox.hq.ansr.com and sandbox.hq.ansr.com
        secure: true,
        sameSite: 'strict',
    },
    prod: {
        env: 'prod',
        domain: '.hq.ansr.com', // This allows sharing between *.hq.ansr.com and hq.ansr.com
        secure: true,
        sameSite: 'strict',
    },
};

/**
 * Get current environment from hostname
 * @returns {string} environment name (localhost, uat, sandbox, prod)
 */
export const getCurrentEnvironment = () => {
    const hostname = window.location.hostname;

    // Check for localhost first
    if (hostname === 'localhost') {
        return 'localhost';
    }

    if (hostname.includes('uat.hq.ansr.com')) {
        return 'uat';
    } else if (hostname.includes('sandbox.hq.ansr.com')) {
        return 'sandbox';
    } else if (hostname.includes('hq.ansr.com')) {
        return 'prod';
    }

    // Default to localhost for unknown domains (development)
    return 'localhost';
};

/**
 * Get cookie configuration for current environment
 * @returns {Object} cookie configuration
 */
const getCookieConfig = () => {
    const environment = getCurrentEnvironment();
    return ENVIRONMENTS[environment] || ENVIRONMENTS.prod;
};

/**
 * Set a cookie with proper domain configuration
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number|null} days - Days until expiration (default: 7). Pass null for session cookie.
 */
export const setCookie = (name, value, days = 7) => {
    const config = getCookieConfig();

    // Build cookie string based on environment
    let cookieString = `${name}=${value}; path=/`;

    // Add expiration only if days is provided (not null)
    if (days !== null) {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        cookieString += `; expires=${expires.toUTCString()}`;
    }

    // Add domain only if it's not empty (not localhost)
    if (config.domain) {
        cookieString += `; domain=${config.domain}`;
    }

    // Add secure and sameSite attributes
    cookieString += `; secure=${config.secure}; samesite=${config.sameSite}`;

    document.cookie = cookieString;
};

/**
 * Set a session cookie (expires when browser is closed)
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 */
export const setSessionCookie = (name, value) => {
    setCookie(name, value, null);
};

/**
 * Get cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }

    return null;
};

/**
 * Delete cookie by name
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name) => {
    const config = getCookieConfig();

    // Build cookie deletion string based on environment
    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

    // Add domain only if it's not empty (not localhost)
    if (config.domain) {
        cookieString += `; domain=${config.domain}`;
    }

    document.cookie = cookieString;
};

/**
 * Token-specific cookie operations
 */
export const setAuthToken = (token) => {
    const config = getCookieConfig();
    setSessionCookie(config.env + '_' + TOKEN_COOKIE_NAME, token);
};

export const getAuthToken = () => {
    const config = getCookieConfig();
    return getCookie(config.env + '_' + TOKEN_COOKIE_NAME);
};

export const removeAuthToken = () => {
    const config = getCookieConfig();
    deleteCookie(config.env + '_' + TOKEN_COOKIE_NAME);
};

/**
 * Company referer cookie operations
 */
export const setCompanyReferer = (companyReferer) => {
    const config = getCookieConfig();
    setSessionCookie(config.env + '_' + COMPANY_REFERER_COOKIE_NAME, companyReferer);
};

export const getCompanyReferer = () => {
    const config = getCookieConfig();
    return getCookie(config.env + '_' + COMPANY_REFERER_COOKIE_NAME);
};

export const removeCompanyReferer = () => {
    const config = getCookieConfig();
    deleteCookie(config.env + '_' + COMPANY_REFERER_COOKIE_NAME);
};

/**
 * Clear all authentication cookies
 */
export const clearAuthCookies = () => {
    removeAuthToken();
    removeCompanyReferer();
};

/**
 * Check if running in localhost environment
 * @returns {boolean} True if running on localhost
 */
export const isLocalhost = () => {
    return getCurrentEnvironment() === 'localhost';
};
